/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../ExtensionBaseController',
	'./DataSourcesView',
	'../CanvasExtensionPropertyProvider',
	'../../apiHelpers/SlotAPIHelper',
	'../../dataSources/nls/StringResources'
], function(_, BaseController, DataSourcesView, CanvasExtensionPropertyProvider, SlotAPIHelper, StringResources) {
	'use strict';

	const SOURCE_TYPE = {
		PACKAGE: 'package'
	};

	class DataSourcesController extends BaseController {

		static RELATIONSHIPS_ID = 'relationships';

		constructor(dashboard, appSettings, eventRouter) {
			super(dashboard, appSettings, eventRouter);

			this.featureChecker = this.dashboard.getGlassCoreSvc('.FeatureChecker');
			this.dataSourcesFeature = this.dashboard.getFeature('DataSources');

			this.emptyDatasetRenderer = DataSourcesView.renderEmptyDataset;
			this.MUIDataGridRender = DataSourcesView.renderMUIGrid;
			this.MUIDiagramRender = DataSourcesView.renderMUIDiagram;

		}

		_ensureTempModuleIfNeeded(module){
			const showGridInConsume = this.dashboard.getCanvas().getPropertyValue(CanvasExtensionPropertyProvider.PROP_ID_SHOW_DATA_GRID_IN_CONSUME);
			let promise;
			if (showGridInConsume && this.dashboard.getMode() === this.dashboard.MODES.VIEW ) {
				promise =  module.ensureTemporaryModule( { forceTemporaryModule: true });
			}else{
				promise = Promise.resolve();
			}
			return promise;
		}

		getActiveDataSourceId(throwOnNoDataSource = false) {
			const activeSourceId = this.dataSourcesFeature.getActiveDataSourceId();
			if(throwOnNoDataSource && !activeSourceId){
				throw new Error('shapingmodel:changed triggered, but there is no active data source');
			}
			return activeSourceId;
		}

		getActiveVisualization() {
			const content = this._getSelectedContent();
			return content && content.getFeature('Visualization');
		}

		getActiveVisualizationDataSourceId() {
			const visualization = this.getActiveVisualization();
			const dataSource = visualization && visualization.getDataSource();
			// if there are no active visualization or active visualization does not have a datasource
			// fallback to the active datasource
			return dataSource ? dataSource.getId() : this.getActiveDataSourceId();
		}

		/**
		 * @see ExtensionBaseController
		 */
		getTabContrib(event, eventName, currentTabId){
			return Promise.all([
				this._prepareDataSources(),
				this._getSelectedContent(),
				this._getRelationshipsView()
			])
				.then((deps) => {

					let tabContribs = deps[0];
					let content = deps[1];
					if (!tabContribs.length){
						return null;
					}
					// append the relationships as tab contributions
					tabContribs.push(...deps[2]);

					let selectedTab = null;
					const relationshipsTabSelected = currentTabId === DataSourcesController.RELATIONSHIPS_ID;
					const eventSender = (event && event.sender) || currentTabId;
					// event.name has precedence over eventName due to compound events
					eventName = (event && event.name) || eventName;
					switch(eventName) {
						case 'dataSourcePanel:dataSourceSelected':
							selectedTab = this._getSelectedTabContrib(tabContribs, eventSender, false);
							break;
						case 'shapingmodel:changed':
							// remain in the relationships diagram, otherwise force to the active datasource tab
							selectedTab = this._getSelectedTabContrib(tabContribs, relationshipsTabSelected ? currentTabId :  this.getActiveDataSourceId(true));
							break;
						case 'widget:rerendered':
							// Widget rerendered but if datasource view is the current tab, then data source view should still be the active view.
							// However a force re-render is necessary for the relationships view to reflect any changes.
							selectedTab = this._getSelectedTabContrib(tabContribs, currentTabId, relationshipsTabSelected);
							break;
						case 'change:selections':
							// keep the selection only if the current tab is relationships
							if (relationshipsTabSelected) {
								selectedTab = this._getSelectedTabContrib(tabContribs, eventSender);
							}
							break;
						case 'splitterPanel:show':
							selectedTab = this._getSelectedTabContrib(tabContribs, eventSender);
							break;
						case 'splitterPanel:visibleChange':
							selectedTab = this._getSelectedTabContrib(tabContribs, currentTabId, false);
							break;
						case 'remove':
							selectedTab = this._getSelectedTabContrib(tabContribs, currentTabId);
							break;
					}


					if(!selectedTab && !content){
						let activeSourceId = this.getActiveDataSourceId();
						if(!activeSourceId){
							selectedTab = tabContribs[0];
						}else{
							selectedTab = _.findWhere(tabContribs, { id: activeSourceId });
						}
						selectedTab.isSelectedOnTrayRender = true;
					}

					return tabContribs;
				});
		}

		_getSelectedTabContrib(tabContribs, tabId, forceRender = true) {
			const selectedTab = _.findWhere(tabContribs, { id: tabId });
			if (selectedTab) {
				const selectContext = this._getSelectContext(tabId);
				selectedTab.isSelectedOnTrayRender = true;
				selectedTab.select = this.select.bind(this, forceRender ? { forceRender, ...selectContext } : selectContext);
			}
			return selectedTab;
		}

		/**
		 * Given an array of source model object, returns and array of source objects that the tab control expects.
		 * @return {Promise} promise that will resolve with an array of source object ready for the tab control
		 */
		_prepareDataSources() {
			const whenNamesReady = [];
			const aPrepDataSources = [];
			const getName = (source, item) => source.getLocalizedName().then(name => {
				item.name = name;
				item.state = source.getState(); //State can be updated by the getLocalizedName() call, so set it after that call has finished.
			}).fail(() => {
				item.state = 'error';
			});
			const dataSources = this.dataSourcesFeature.getDataSourceList() || [];

			dataSources.forEach(dataSource => {
				const id = dataSource.getId();
				const item = {
					type: dataSource.getType(),
					id: id,
					select: this.select.bind(this, this._getSelectContext(id))
				};
				aPrepDataSources.push(item);
				whenNamesReady.push(getName(dataSource, item));
			});

			return Promise.all(whenNamesReady).then(() => aPrepDataSources);
		}

		_supportsRelationshipsView() {
			// relationships diagram is only available in authoring mode
			const isAuthoring = this.dashboard.getMode() === this.dashboard.MODES.EDIT;
			// @todo: when removing the feature flag, the feature flag needs to be replaced with an app config (default: disabled)
			return isAuthoring && (this.featureChecker.checkValue('dashboard', 'RelationshipsView', 'enabled') ||
				this.dashboard.getConfiguration('showRelationships'));
		}

		_getSelectContext(id) {
			const context = { id };
			if (id === DataSourcesController.RELATIONSHIPS_ID) {
				const visualization = this.getActiveVisualization();
				const slots = visualization && visualization.getSlots();
				const dataItems = slots && slots.getDataItemList();
				if (dataItems && dataItems.length) {
					const tableIds = {};
					context.queryItemIds = dataItems.filter(dataItem => !SlotAPIHelper.isMultiMeasuresSeriesOrValueDataItem(dataItem))
						.map(dataItem => {
							const column = dataItem.getMetadataColumn();
							const tableId = column.getTableId();
							tableId && (tableIds[tableId] = null);
							return column.getId();
						});
					// collect unique query subject ids
					context.querySubjectIds = Object.keys(tableIds);
				}
			}
			return context;
		}

		_getRelationshipsView() {
			let contribs = [];
			if (this._supportsRelationshipsView()) {
				contribs.push({
					id: DataSourcesController.RELATIONSHIPS_ID,
					name: StringResources.get('relationships_title'),
					select: this.select.bind(this, this._getSelectContext(DataSourcesController.RELATIONSHIPS_ID))
				});
			}
			return Promise.resolve(contribs);
		}

		/**
		 * Renders the data source grid view.
		 */
		render(context, container){
			const sourceId = context.id === DataSourcesController.RELATIONSHIPS_ID ? this.getActiveVisualizationDataSourceId() : context.id;
			const source = this.dataSourcesFeature.getDataSource(sourceId);
			if (!source || source.getType() === SOURCE_TYPE.PACKAGE) {
				return this.emptyDatasetRenderer(container);
			}
			return source.getModule().then(module => {
				// enableDataQuality is a string
				const enableDataQuality = this.appSettings.options && this.appSettings.options.config ? this.appSettings.options.config.enableDataQuality=='true' : true;
				return this._ensureTempModuleIfNeeded(module).then(() => {
					if (context.id === DataSourcesController.RELATIONSHIPS_ID) {
						return this.MUIDiagramRender(module, container, context);
					} else {
						return this.MUIDataGridRender(module, enableDataQuality, container, this.dashboard);
					}
				});
			});
		}

		/**
		 * @see ExtensionBaseController
		 */
		select(context, currentTabId, container){
			if(currentTabId === context.id && !context.forceRender){
				return Promise.resolve(false);
			}

			if (context.id !== DataSourcesController.RELATIONSHIPS_ID) {
				this.dataSourcesFeature.setActiveDataSourceId(context.id);
				const dataSource = this.dataSourcesFeature.getDataSource(context.id);
				this.eventRouter.trigger('dataSourceGrid:dataSourceSelected', dataSource);
			}
			return this.render(context, container);
		}

		isSupported(){
			const showGridInConsume = this.dashboard.getCanvas().getPropertyValue(CanvasExtensionPropertyProvider.PROP_ID_SHOW_DATA_GRID_IN_CONSUME);
			const mode = this.dashboard.getMode();
			if (showGridInConsume &&  mode === this.dashboard.MODES.VIEW){
				return true;
			}else{
				return super.isSupported();
			}
		}


	}

	return DataSourcesController;
});
